import { ElMessage } from 'element-plus';
import type { Ref } from 'vue';
import { ajax } from '@/api';
import { explorerRandomPath } from './scene';
import type { LevelItem, LevelSource } from './types';

type RouterLike = {
  push: (to: string | { path: string; query?: Record<string, unknown> }) => void | Promise<void>;
};

export const useLevelExplorerRandomSession = (options: {
  activeLevel: Ref<LevelItem | null>;
  activeSource: Ref<LevelSource>;
  messages: Ref<any[]>;
  messageContent: Ref<string>;
  showProfile: Ref<boolean>;
  showEdit: Ref<boolean>;
  showRandomLevel: Ref<boolean>;
  requireLogin: () => boolean;
  leaveVisitStatsRoute: () => void;
  goHome: () => void;
  router: RouterLike;
  loadArchive: () => void;
}) => {
  const {
    activeLevel,
    activeSource,
    messages,
    messageContent,
    showProfile,
    showEdit,
    showRandomLevel,
    requireLogin,
    leaveVisitStatsRoute,
    goHome,
    router,
    loadArchive
  } = options;

  const randomAccessPayload = () => {
    const randomToken = activeSource.value === 'random' ? activeLevel.value?.randomToken : '';
    if (!randomToken) {
      ElMessage.warning('请先通过随机探索进入层级');
      return null;
    }
    return { randomToken };
  };

  const handleLevelChange = (res: any) => {
    if (res.code === 0) {
      const randomToken = res.data?.randomToken || activeLevel.value?.randomToken || '';
      activeLevel.value = { ...res.data, randomToken };
      ElMessage.success(res.msg);
      loadArchive();
    } else {
      ElMessage.error(res.msg);
    }
  };

  const loadMessages = () => {
    if (!activeLevel.value) return;
    const payload = randomAccessPayload();
    if (!payload) return;
    ajax({
      url: 'level/random/message/list',
      method: 'POST',
      data: payload,
      isLoading: false,
      success: (res: any) => {
        if (res.code === 0) messages.value = res.dataList || [];
      }
    });
  };

  const loadRandomLevel = () => {
    activeLevel.value = null;
    messages.value = [];
    activeSource.value = 'random';
    ajax({
      url: 'level/random',
      method: 'GET',
      success: (res: any) => {
        if (res.code === 0) {
          showProfile.value = false;
          leaveVisitStatsRoute();
          showEdit.value = false;
          activeLevel.value = res.data;
          activeSource.value = 'random';
          loadMessages();
        } else {
          ElMessage.warning(res.msg);
        }
      }
    });
  };

  const randomLevel = () => {
    showProfile.value = false;
    showEdit.value = false;
    if (showRandomLevel.value) {
      loadRandomLevel();
      return;
    }
    router.push(explorerRandomPath);
  };

  const leaveLevel = () => {
    activeLevel.value = null;
    messages.value = [];
    activeSource.value = 'archive';
    goHome();
  };

  const occupyLevel = () => {
    if (!activeLevel.value || !requireLogin()) return;
    const payload = randomAccessPayload();
    if (!payload) return;
    ajax({
      url: 'level/random/occupy',
      method: 'POST',
      data: payload,
      success: handleLevelChange
    });
  };

  const guardLevel = () => {
    if (!activeLevel.value || !requireLogin()) return;
    const payload = randomAccessPayload();
    if (!payload) return;
    ajax({
      url: 'level/random/guard',
      method: 'POST',
      data: payload,
      success: handleLevelChange
    });
  };

  const toggleFavorite = () => {
    if (!activeLevel.value || !requireLogin()) return;
    const payload = randomAccessPayload();
    if (!payload) return;
    ajax({
      url: 'level/random/favorite/toggle',
      method: 'POST',
      data: payload,
      success: (res: any) => {
        if (res.code === 0) ElMessage.success(res.msg);
        else ElMessage.error(res.msg);
      }
    });
  };

  const sendMessage = () => {
    if (!activeLevel.value || !requireLogin()) return;
    if (!messageContent.value.trim()) {
      ElMessage.warning('请输入留言内容');
      return;
    }
    const payload = randomAccessPayload();
    if (!payload) return;
    ajax({
      url: 'level/random/message/add',
      method: 'POST',
      data: { ...payload, content: messageContent.value.trim() },
      success: (res: any) => {
        if (res.code === 0) {
          messageContent.value = '';
          loadMessages();
          ElMessage.success(res.msg);
        } else {
          ElMessage.error(res.msg);
        }
      }
    });
  };

  return {
    loadRandomLevel,
    randomLevel,
    leaveLevel,
    occupyLevel,
    guardLevel,
    toggleFavorite,
    loadMessages,
    sendMessage
  };
};
